import { startTransition, useCallback, useEffect, useEffectEvent, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type {
  MessageTypeName,
  OrganizationMemberRoleName,
  PlatformTypeName,
  ReportMessageListResponse,
  ReportMessageOptionsResponse,
  ReportMessagePageSize,
  ReportMessageSortName,
  ReportMessageSummaryResponse,
} from "@multi-chat/shared";
import {
  getIncomingReportMessagesSummary,
  getReportMessagesOptions,
  listIncomingReportMessages,
} from "../../api/reports-api.js";
import {
  clearIncomingFilters,
  hasActiveIncomingFilters,
  incomingReportHref,
  parseIncomingReportQuery,
  type IncomingReportQuery,
} from "./incoming-report-query.js";

const SEARCH_DEBOUNCE_MS = 280;

export function useIncomingMessagesReport(input: {
  organizationId: number;
  role: OrganizationMemberRoleName;
}): {
  query: IncomingReportQuery;
  list: ReportMessageListResponse | null;
  summary: ReportMessageSummaryResponse | null;
  options: ReportMessageOptionsResponse | null;
  loading: boolean;
  error: unknown;
  filtersActive: boolean;
  canAccess: boolean;
  dateFrom: string;
  dateTo: string;
  setSearch: (search: string) => void;
  setDateRange: (from: string, to: string) => void;
  setPlatform: (platform: PlatformTypeName | undefined) => void;
  setEndpointId: (endpointId: number | undefined) => void;
  setMessageType: (messageType: MessageTypeName | undefined) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: ReportMessagePageSize) => void;
  setSort: (sort: ReportMessageSortName) => void;
  resetFilters: () => void;
  refresh: () => void;
} {
  const canAccess = input.role === "OWNER" || input.role === "ADMIN";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const searchParamsKey = searchParams.toString();
  const query = useMemo(
    () => parseIncomingReportQuery(searchParamsKey),
    [searchParamsKey],
  );

  const [searchDraft, setSearchDraft] = useState(query.search);
  const [debouncedSearch, setDebouncedSearch] = useState(query.search);
  const [list, setList] = useState<ReportMessageListResponse | null>(null);
  const [summary, setSummary] = useState<ReportMessageSummaryResponse | null>(null);
  const [options, setOptions] = useState<ReportMessageOptionsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    setSearchDraft(query.search);
    setDebouncedSearch(query.search);
  }, [query.search]);

  const syncSearchToUrl = useEffectEvent((nextSearch: string) => {
    if (nextSearch === query.search) return;
    navigate(incomingReportHref({ ...query, search: nextSearch, page: 1 }), { replace: true });
  });

  useEffect(() => {
    const handle = window.setTimeout(() => {
      const next = searchDraft.trim();
      setDebouncedSearch(next);
      syncSearchToUrl(next);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
    // syncSearchToUrl is useEffectEvent — omit from deps (new identity every render).
  }, [searchDraft]);

  const applyQuery = useCallback(
    (next: IncomingReportQuery) => {
      navigate(incomingReportHref(next), { replace: true });
    },
    [navigate],
  );

  const patchQuery = useCallback(
    (patch: Partial<IncomingReportQuery>, resetPage = true) => {
      applyQuery({
        ...query,
        ...patch,
        page: resetPage ? 1 : (patch.page ?? query.page),
      });
    },
    [applyQuery, query],
  );

  const load = useEffectEvent(async (signal: AbortSignal) => {
    if (!canAccess) return;
    setLoading(true);
    setError(null);
    const sharedFilters = {
      ...(query.from && query.to ? { fromDate: query.from, toDate: query.to } : {}),
      platform: query.platform,
      endpointId: query.endpointId,
      messageType: query.messageType,
      search: debouncedSearch.trim() || undefined,
    };
    try {
      const [listRes, summaryRes, optionsRes] = await Promise.all([
        listIncomingReportMessages(
          input.organizationId,
          {
            ...sharedFilters,
            page: query.page,
            pageSize: query.pageSize,
            sort: query.sort,
          },
          signal,
        ),
        getIncomingReportMessagesSummary(input.organizationId, sharedFilters, signal),
        getReportMessagesOptions(input.organizationId, {}, signal),
      ]);
      if (signal.aborted) return;
      startTransition(() => {
        setList(listRes);
        setSummary(summaryRes);
        setOptions(optionsRes);
      });
    } catch (err) {
      if (signal.aborted) return;
      setError(err);
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  });

  useEffect(() => {
    if (!canAccess) return;
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
    // Do NOT depend on `load`: useEffectEvent returns a new function identity every
    // render (React 19). Listing it here aborts+refetches forever while idle.
  }, [
    canAccess,
    input.organizationId,
    query.from,
    query.to,
    query.platform,
    query.endpointId,
    query.messageType,
    query.page,
    query.pageSize,
    query.sort,
    debouncedSearch,
    refreshToken,
  ]);

  return {
    query,
    list,
    summary,
    options,
    loading,
    error,
    filtersActive: hasActiveIncomingFilters({ ...query, search: searchDraft }),
    canAccess,
    dateFrom: query.from ?? summary?.fromDate ?? "",
    dateTo: query.to ?? summary?.toDate ?? "",
    setSearch: setSearchDraft,
    setDateRange: (from, to) => {
      if (!from || !to) {
        const next = { ...query };
        delete next.from;
        delete next.to;
        applyQuery({ ...next, page: 1 });
        return;
      }
      patchQuery({ from, to });
    },
    setPlatform: (platform) => patchQuery({ platform }),
    setEndpointId: (endpointId) => patchQuery({ endpointId }),
    setMessageType: (messageType) => patchQuery({ messageType }),
    setPage: (page) => patchQuery({ page }, false),
    setPageSize: (pageSize) => patchQuery({ pageSize, page: 1 }),
    setSort: (sort) => patchQuery({ sort }),
    resetFilters: () => applyQuery(clearIncomingFilters(query)),
    refresh: () => setRefreshToken((n) => n + 1),
  };
}
